import React, { useRef, useState } from 'react';
import { 
  Download, Printer, Coffee, Gift, QrCode, MapPin, Clock, Phone,
  Smartphone, ChevronRight, Award
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import html2canvas from 'html2canvas';

/**
 * Partner Flyer Page - One-page printable handout for recruiting taxi/tour drivers
 * Sales reps can print this or download as an image to share
 * Access via /partner-flyer or /partner-flyer?rep=REP-XXXX
 */
export default function PartnerFlyerPage() {
  const flyerRef = useRef(null);
  const [downloading, setDownloading] = useState(false);
  
  // Get rep code from URL if present
  const urlParams = new URLSearchParams(window.location.search);
  const repCode = urlParams.get('rep') || '';

  const handlePrint = () => {
    window.print();
  };

  const handleDownload = async () => {
    if (!flyerRef.current) return;
    
    setDownloading(true);
    try {
      const canvas = await html2canvas(flyerRef.current, {
        scale: 2,
        backgroundColor: '#ffffff',
        useCORS: true,
        width: 816, // A4 width at 96 DPI
        height: 1056 // A4 height at 96 DPI
      });
      
      const link = document.createElement('a');
      link.download = `Bulaccino-Partner-Flyer${repCode ? `-${repCode}` : ''}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
      toast.success('Flyer downloaded!');
    } catch (err) {
      console.error('Download failed:', err);
      toast.error('Failed to download flyer');
    }
    setDownloading(false);
  };

  // Generate signup URL with rep attribution
  const signupUrl = repCode 
    ? `bulaccinocafe.com/partner-signup?rep=${repCode}` 
    : 'bulaccinocafe.com/partner-signup';

  return (
    <div className="min-h-screen bg-gray-200 py-8 px-4">
      {/* Action Buttons - Hidden when printing */}
      <div className="print:hidden max-w-[816px] mx-auto mb-6 flex gap-3 justify-center">
        <Button onClick={handlePrint} size="lg" className="shadow-lg">
          <Printer className="w-5 h-5 mr-2" />
          Print Flyer
        </Button>
        <Button onClick={handleDownload} size="lg" variant="outline" disabled={downloading}>
          <Download className="w-5 h-5 mr-2" />
          {downloading ? 'Downloading...' : 'Download Image'}
        </Button>
      </div>

      {/* Flyer Content - A4 Size Ratio */}
      <div 
        ref={flyerRef}
        className="bg-white shadow-2xl print:shadow-none mx-auto overflow-hidden"
        style={{ 
          width: '816px', 
          height: '1056px',
          fontFamily: 'system-ui, -apple-system, sans-serif'
        }}
      >
        {/* Header with Logo */}
        <div className="bg-gradient-to-r from-green-700 via-green-600 to-green-700 text-white px-8 py-6 text-center relative overflow-hidden">
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-0 left-0 w-32 h-32 bg-white/20 rounded-full -translate-x-1/2 -translate-y-1/2"></div>
            <div className="absolute bottom-0 right-0 w-48 h-48 bg-white/20 rounded-full translate-x-1/4 translate-y-1/4"></div>
          </div>
          <div className="relative">
            <img 
              src="/images/bulaccino-logo-white.png" 
              alt="Bulaccino Cafe" 
              className="h-16 mx-auto mb-2"
              onError={(e) => { e.target.style.display = 'none'; }}
            />
            <h1 className="text-4xl font-bold tracking-tight">BULACCINO CAFE</h1>
            <p className="text-green-200 mt-1">Fiji's Finest Coffee Experience</p>
          </div>
        </div>

        {/* Main Banner */}
        <div className="bg-gradient-to-r from-amber-400 via-amber-500 to-orange-400 py-6 text-center">
          <h2 className="text-3xl font-black text-white mb-1">PARTNER PROGRAM</h2>
          <p className="text-white/90 text-lg">Earn FREE Coffee & Lifetime Discounts!</p>
        </div>

        {/* Two Column Content */}
        <div className="grid grid-cols-2 gap-6 p-8">
          {/* Left Column - Benefits */}
          <div className="space-y-5">
            <h3 className="text-2xl font-bold text-green-800 border-b-2 border-green-200 pb-2">
              What You Earn
            </h3>

            {/* Free Coffee */}
            <div className="bg-amber-50 rounded-xl p-4 border-2 border-amber-200">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-12 h-12 rounded-full bg-amber-500 flex items-center justify-center">
                  <Coffee className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h4 className="font-bold text-lg text-amber-800">Free Coffee</h4>
                  <p className="text-amber-700 text-sm">Every 100 FJD tourists spend</p>
                </div>
              </div>
              <p className="text-gray-600 text-sm ml-15">
                ~20 tourists = 1 free coffee!
              </p>
            </div>

            {/* Tourist Discount */}
            <div className="bg-green-50 rounded-xl p-4 border-2 border-green-200">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-12 h-12 rounded-full bg-green-600 flex items-center justify-center">
                  <Gift className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h4 className="font-bold text-lg text-green-800">Tourists Save 5%</h4>
                  <p className="text-green-700 text-sm">When they use your code</p>
                </div>
              </div>
              <p className="text-gray-600 text-sm ml-15">
                Win-win for you and them!
              </p>
            </div>

            {/* Lifetime Tiers */}
            <div className="bg-purple-50 rounded-xl p-4 border-2 border-purple-200">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-12 h-12 rounded-full bg-purple-600 flex items-center justify-center">
                  <Award className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h4 className="font-bold text-lg text-purple-800">Lifetime Discounts</h4>
                  <p className="text-purple-700 text-sm">Unlock as tourists spend</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div className="bg-white rounded p-2 text-center">
                  <span className="font-bold text-amber-700">10% OFF</span>
                  <span className="text-gray-500 text-xs block">at 1,000 FJD</span>
                </div>
                <div className="bg-white rounded p-2 text-center">
                  <span className="font-bold text-gray-600">12.5% OFF</span>
                  <span className="text-gray-500 text-xs block">at 2,000 FJD</span>
                </div>
                <div className="bg-white rounded p-2 text-center">
                  <span className="font-bold text-yellow-600">15% OFF</span>
                  <span className="text-gray-500 text-xs block">at 3,000 FJD</span>
                </div>
                <div className="bg-white rounded p-2 text-center">
                  <span className="font-bold text-purple-600">20% OFF</span>
                  <span className="text-gray-500 text-xs block">at 5,000 FJD</span>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column - How It Works */}
          <div className="space-y-5">
            <h3 className="text-2xl font-bold text-green-800 border-b-2 border-green-200 pb-2">
              How It Works
            </h3>

            <div className="space-y-4">
              {/* Step 1 */}
              <div className="flex gap-4 items-start">
                <div className="w-10 h-10 rounded-full bg-green-600 text-white flex items-center justify-center font-bold flex-shrink-0 text-lg">
                  1
                </div>
                <div>
                  <h4 className="font-bold text-gray-800">Sign Up (30 seconds)</h4>
                  <p className="text-gray-600 text-sm">Scan QR code or visit the link below</p>
                </div>
              </div>

              {/* Step 2 */}
              <div className="flex gap-4 items-start">
                <div className="w-10 h-10 rounded-full bg-green-600 text-white flex items-center justify-center font-bold flex-shrink-0 text-lg">
                  2
                </div>
                <div>
                  <h4 className="font-bold text-gray-800">Get Your Code</h4>
                  <p className="text-gray-600 text-sm">Receive unique code like BULA-1234</p>
                </div>
              </div>

              {/* Step 3 */}
              <div className="flex gap-4 items-start">
                <div className="w-10 h-10 rounded-full bg-green-600 text-white flex items-center justify-center font-bold flex-shrink-0 text-lg">
                  3
                </div>
                <div>
                  <h4 className="font-bold text-gray-800">Share with Tourists</h4>
                  <p className="text-gray-600 text-sm">"Use code BULA-1234 for 5% off!"</p>
                </div>
              </div>

              {/* Step 4 */}
              <div className="flex gap-4 items-start">
                <div className="w-10 h-10 rounded-full bg-amber-500 text-white flex items-center justify-center font-bold flex-shrink-0 text-lg">
                  4
                </div>
                <div>
                  <h4 className="font-bold text-gray-800">Earn Rewards!</h4>
                  <p className="text-gray-600 text-sm">Get SMS every time a tourist uses your code</p>
                </div>
              </div>
            </div>

            {/* QR Code Placeholder */}
            <div className="bg-gray-50 rounded-xl p-4 border-2 border-dashed border-gray-300 text-center">
              <div className="w-32 h-32 mx-auto bg-white rounded-lg border-2 border-gray-200 flex items-center justify-center mb-2">
                <QrCode className="w-20 h-20 text-green-600" />
              </div>
              <p className="font-bold text-gray-800 text-sm">SCAN TO SIGN UP</p>
              <p className="text-xs text-gray-500 mt-1 font-mono break-all">{signupUrl}</p>
              {repCode && (
                <p className="text-xs text-green-600 mt-1">Rep: {repCode}</p>
              )}
            </div>
          </div>
        </div>

        {/* Bottom Section */}
        <div className="px-8 pb-6 mt-auto">
          {/* Perfect For */}
          <div className="bg-blue-50 rounded-xl p-4 mb-4">
            <h4 className="font-bold text-blue-800 text-center mb-3">Perfect For</h4>
            <div className="flex justify-center gap-8 text-sm text-blue-700">
              <span>🚕 Taxi Drivers</span>
              <span>🚌 Tour Operators</span>
              <span>🏨 Hotel Staff</span>
              <span>🚗 Car Rentals</span>
            </div>
          </div>

          {/* Cafe Info Footer */}
          <div className="flex items-center justify-between bg-green-700 text-white rounded-xl p-4">
            <div className="flex items-center gap-6">
              <div className="flex items-center gap-2">
                <MapPin className="w-5 h-5 text-green-300" />
                <span className="text-sm">Martintar, Nadi</span>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="w-5 h-5 text-green-300" />
                <span className="text-sm">6:30am - 6pm</span>
              </div>
            </div>
            <div className="text-right">
              <p className="font-bold">bulaccinocafe.com</p>
              <p className="text-green-300 text-xs">FREE to join • No catches</p>
            </div>
          </div>
        </div>
      </div>

      {/* Instructions for Sales Rep - Hidden when printing */}
      <div className="print:hidden max-w-[816px] mx-auto mt-6 bg-amber-50 rounded-xl p-6 border border-amber-200">
        <h3 className="font-bold text-amber-800 mb-3">📋 Sales Rep Instructions</h3>
        <ul className="text-sm text-gray-700 space-y-2">
          <li>• <strong>Print this flyer</strong> and hand it to taxi drivers, tour operators, and hotel staff</li>
          <li>• <strong>Add your QR code:</strong> Print your QR code separately and tape it over the placeholder, or generate a personalized flyer</li>
          <li>• <strong>Download as image:</strong> Save to your phone and share via WhatsApp</li>
          <li>• <strong>Track signups:</strong> Your rep code ({repCode || 'REP-XXXX'}) will be in the signup URL to track your referrals</li>
        </ul>
        {!repCode && (
          <div className="mt-4 p-3 bg-white rounded-lg border border-amber-300">
            <p className="text-amber-700 text-sm">
              <strong>Tip:</strong> Add your rep code to the URL: <code className="bg-amber-100 px-1 rounded">/partner-flyer?rep=YOUR-CODE</code>
            </p>
          </div>
        )}
      </div>

      {/* Print Styles */}
      <style>{`
        @media print {
          @page {
            size: letter;
            margin: 0;
          }
          body { 
            -webkit-print-color-adjust: exact; 
            print-color-adjust: exact;
            margin: 0;
            padding: 0;
          }
          .print\\:hidden { display: none !important; }
          .print\\:shadow-none { box-shadow: none !important; }
        }
      `}</style>
    </div>
  );
}
